import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Button } from 'react-bootstrap';

// Musical instruments data
const instruments = [
  { name: 'Piano', emoji: '🎹', sound: '/sounds/piano.mp3' },
  { name: 'Guitar', emoji: '🎸', sound: '/sounds/guitar.mp3' },
  { name: 'Drums', emoji: '🥁', sound: '/sounds/drums.mp3' },
  { name: 'Violin', emoji: '🎻', sound: '/sounds/violin.mp3' },
  { name: 'Trumpet', emoji: '🎺', sound: '/sounds/trumpet.mp3' },
  { name: 'Saxophone', emoji: '🎷', sound: '/sounds/saxophone.mp3' },
  { name: 'Flute', emoji: '🪈', sound: '/sounds/flute.mp3' },
  { name: 'Maracas', emoji: '🪇', sound: '/sounds/maracas.mp3' }
];

export const MusicalInstrumentsGame = ({ handleClose, onGameComplete }) => {
  const [targetInstrument, setTargetInstrument] = useState(null);
  const [playedInstruments, setPlayedInstruments] = useState([]);
  const [correctCount, setCorrectCount] = useState(0);
  const [playingSound, setPlayingSound] = useState(false);

  const correctSound = new Audio('/sounds/correct.mp3');
  const errorSound = new Audio('/sounds/error.mp3');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    const savedVolume = localStorage.getItem('tapGameVolume');
    if (savedVolume !== null) {
      setVolume(parseFloat(savedVolume));
    }
  }, []);

  useEffect(() => {
    playNewInstrument();
  }, []);

  // Speak instruction when target instrument changes
  useEffect(() => {
    if (targetInstrument && !isMuted) {
      const speakTimeout = setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(
          `Find the ${targetInstrument.name}`
        );
        utterance.rate = 0.8;
        utterance.volume = volume;
        speechSynthesis.speak(utterance);
      }, 2500);

      return () => clearTimeout(speakTimeout);
    }
  }, [targetInstrument, isMuted, volume]);

  const playNewInstrument = (currentPlayed = playedInstruments) => {
    const remaining = instruments.filter(i => !currentPlayed.includes(i.name));
    if (remaining.length === 0) {
      const stars = 3;
      const score = instruments.length * 12;
      confetti({ particleCount: 250, spread: 100 });
      setTimeout(() => {
        onGameComplete?.(stars, score);
        handleClose?.();
      }, 2000);
      return;
    }

    const next = remaining[Math.floor(Math.random() * remaining.length)];
    setTargetInstrument(next);
    
    setTimeout(() => {
      playInstrumentSound(next.sound);
    }, 800);
  };

  const playInstrumentSound = (soundPath) => {
    if (isMuted) return;
    
    setPlayingSound(true);
    const audio = new Audio(soundPath);
    audio.volume = volume;
    audio.onended = () => setPlayingSound(false);
    audio.onerror = (err) => {
      console.log('Audio play failed:', err);
      setPlayingSound(false);
    };
    audio.play().catch(err => {
      console.log('Audio play error:', err);
      setPlayingSound(false);
    });
  };

  const handleInstrumentClick = (instrument) => {
    if (instrument.name === targetInstrument.name) {
      if (!isMuted) {
        correctSound.volume = volume;
        correctSound.play();
      }
      
      const updatedPlayed = [...playedInstruments, instrument.name];
      setCorrectCount(correctCount + 1);
      setPlayedInstruments(updatedPlayed);
      
      setTimeout(() => {
        playNewInstrument(updatedPlayed);
      }, 1500);
    } else {
      if (!isMuted) {
        errorSound.volume = volume;
        errorSound.play();
      }
    }
  };

  const replaySound = () => {
    if (targetInstrument) {
      playInstrumentSound(targetInstrument.sound);
    }
  };

  return (
    <div className="tap-letter-board-bg">
      <div className="tap-header-bar mb-1">
        <div className="tap-score-box">
          <span role="img" aria-label="score" style={{ fontSize: 22, marginRight: 6 }}>🎵</span>
          <span className="tap-score-label">Score</span>
          <span className="tap-score-value">{correctCount}/{instruments.length}</span>
        </div>
        <div className="tap-letter-target">
          <span className="tap-letter-label">
            {playingSound ? '🔊 Listening...' : '🎧 Find:'}
          </span>
          <span className="tap-letter-value">{targetInstrument?.name || ''}</span>
        </div>
        <div className="tap-volume-box">
          <span role="img" aria-label="volume" style={{ fontSize: 20, marginRight: 4 }}>🔊</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => {
              const newVol = parseFloat(e.target.value);
              setVolume(newVol);
              localStorage.setItem('tapGameVolume', newVol);
            }}
            style={{ width: '90px', verticalAlign: 'middle' }}
          />
          <span className="tap-volume-value">{Math.round(volume * 100)}%</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ms-2"
            style={{ borderRadius: 12, padding: '2px 8px', marginLeft: 8 }}
            title="Replay Sound"
            onClick={replaySound}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20, verticalAlign: 'middle' }}>replay</span>
          </button>
        </div>
      </div>

      <div className="tap-letter-btn-grid">
        {instruments.map(instrument => (
          <button
            key={instrument.name}
            className={`btn m-1 letter-btn ${playedInstruments.includes(instrument.name) ? 'btn-success selected-correct' : 'btn-outline-primary'}`}
            onClick={() => !playedInstruments.includes(instrument.name) && handleInstrumentClick(instrument)}
            disabled={playedInstruments.includes(instrument.name)}
            style={{
              width: '100px',
              height: '110px',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <span style={{ fontSize: '3rem' }}>{instrument.emoji}</span>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', marginTop: 4 }}>{instrument.name}</span>
          </button>
        ))}
      </div>

      <style>{`
        .tap-letter-board-bg {
          background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
          background-size: cover, cover;
          background-position: center, center;
          border-radius: 32px;
          box-shadow: 0 6px 32px #e0c68a33;
          padding: 32px 12px 24px 12px;
          margin: 0 auto;
          max-width: 900px;
          width: 98%;
          min-height: 400px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
        }
        @keyframes board-pop {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .tap-header-bar {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: 20px;
          box-shadow: 0 2px 8px rgba(224,198,138,0.10);
          padding: 1px 18px 18px 18px;
          margin-bottom: 18px;
          gap: 10px;
        }
        .tap-score-box,
        .tap-volume-box {
          display: flex;
          align-items: center;
          border-radius: 14px;
          padding: 6px 14px;
          font-weight: 700;
          font-size: 1.1rem;
          color: #b8860b;
          background: rgba(255, 255, 255, 0.86);
          min-width: 110px;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
        }
        .tap-score-label {
          margin-right: 6px;
          font-size: 1rem;
          color: #b8860b;
        }
        .tap-score-value {
          font-size: 1.1rem;
          color: #1976d2;
          margin-left: 4px;
          font-weight: 700;
        }
        .tap-letter-target {
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(224,198,138,0.92);
          border-radius: 18px;
          padding: 8px 32px;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          margin: 0 18px;
          min-width: 180px;
          flex: 1;
        }
        .tap-letter-label {
          font-size: 1.1rem;
          color: #3e2723;
          margin-right: 8px;
          font-weight: 600;
        }
        .tap-letter-value {
          font-size: 1.5rem;
          color: #1976d2;
          font-weight: 900;
          text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
        }
        .tap-volume-value {
          margin-left: 8px;
          font-size: 1rem;
          color: #1976d2;
          font-weight: 700;
          min-width: 36px;
          display: inline-block;
          text-align: right;
        }
        .tap-letter-btn-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          width: 100%;
          min-height: 180px;
          margin-top: 10px;
          gap: 8px;
        }
        .letter-btn {
          padding: 0;
          border-radius: 18px !important;
          background: linear-gradient(135deg,rgb(250, 248, 240) 0%,rgb(248, 228, 166) 100%) !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.10);
          border: 2px solid rgba(223, 214, 192, 0.65) !important;
          transition: transform 0.1s, box-shadow 0.2s;
        }
        .letter-btn:hover {
          transform: translateY(-5px) scale(1.05);
        }
        .selected-correct {
          background: #000000 !important;
          border-color: rgb(0, 245, 253) !important;
          color: #fff !important;
          box-shadow: 0 0 16px 2px rgba(122, 255, 244, 0.6);
        }
        @media (max-width: 700px) {
          .tap-header-bar {
            flex-direction: column;
            gap: 10px;
            padding-bottom: 10px;
          }
          .tap-letter-target {
            min-width: 0;
            width: 100%;
            padding: 8px 0;
            margin: 8px 0;
          }
          .letter-btn {
            width: 80px !important;
            height: 90px !important;
          }
        }
        @media (max-width: 480px) {
          .tap-letter-board-bg {
            padding: 10px;
          }
          .letter-btn {
            width: 70px !important;
            height: 80px !important;
          }
        }
      `}</style>
    </div>
  );
};
